import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab, styled, useTheme } from '@mui/material';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { type SyntheticEvent, useCallback, useState } from 'react';
import { Ancestor } from '#enums/ancestors.ts';
import { type DiscoverMovieFilters, type DiscoverTvFilters } from '#types.js';
import { FiltersQuery } from '../Filters/index.ts';
import { SlideoutDrawer } from '../SlideoutDrawer/index.tsx';

export type FiltersDrawerProps = {
  addToShortcuts?: boolean;
  filtersParam?: DiscoverMovieFilters | DiscoverTvFilters;
  open: boolean;
  setFiltersParam: (
    value?: (DiscoverMovieFilters | DiscoverTvFilters) & { shortcutName?: string },
    pathnameOverride?: string,
  ) => void;
  setOpen: (value: boolean) => void;
  title: string;
  type: ScreenType;
};

const FiltersContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

export const FiltersDrawer = ({
  addToShortcuts,
  filtersParam = {},
  open,
  setFiltersParam,
  setOpen,
  title,
  type,
}: FiltersDrawerProps) => {
  const theme = useTheme();
  const [selectedScreenType, setSelectedScreenType] = useState<ScreenType>(type);

  const tabSx = {
    color: theme.supportingTextSecondary,
  };

  const onChange = useCallback<(event: SyntheticEvent, value: ScreenType) => void>(
    (_event, value) => {
      setSelectedScreenType(value);
    },
    [setSelectedScreenType],
  );

  return (
    <SlideoutDrawer
      ancestor={Ancestor.Filters}
      anchor="left"
      open={open}
      setOpen={value => {
        if (!value && selectedScreenType !== type) {
          setSelectedScreenType(type);
        }

        setOpen(value);
      }}
      sx={{
        '.MuiDrawer-paper': {
          [theme.breakpoints.up('md')]: {
            width: '50%',
          },
          [theme.breakpoints.up('lg')]: {
            width: '33%',
          },
          [theme.breakpoints.up('xl')]: {
            width: '25%',
          },
        },
      }}
      title={title}
    >
      <FiltersContainer>
        <TabContext value={selectedScreenType}>
          <TabList aria-label="Screen types" onChange={onChange}>
            <Tab label="Movie" sx={tabSx} value={ScreenType.Movie} />
            <Tab label="TV" sx={tabSx} value={ScreenType.Tv} />
          </TabList>
          <TabPanel sx={{ padding: 0, paddingTop: theme.spacing(2) }} value={selectedScreenType}>
            <FiltersQuery
              addToShortcuts={addToShortcuts}
              enableLoader
              filtersParam={filtersParam}
              setFiltersParam={setFiltersParam}
              setOpenFiltersDrawer={setOpen}
              type={selectedScreenType}
            />
          </TabPanel>
        </TabContext>
      </FiltersContainer>
    </SlideoutDrawer>
  );
};
